import { useEffect, useState } from "react";
import { ClipboardPlus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { roleLabel } from "@/components/UserMenu";

const LINK_LABELS = { "": "No linked record", provider: "Provider", client: "Client", assignment: "Assignment", evaluation: "Evaluation", dc_epl_review: "DC EPL checklist", supervision_pairing: "Supervision pairing", connecteam_exception: "Connecteam exception" };
const empty = { title: "", owner_id: "", due_date: "", due_time: "", priority: "Normal", link_type: "", link_id: "", notes: "", sprint_id: "" };

export const TaskForm = ({ owners, sprints, onCreated, onCancel }) => {
  const [form, setForm] = useState(empty);
  const [options, setOptions] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/tasks/link-options").then((r) => setOptions(r.data)).catch((e) => setError(formatApiError(e)));
  }, []);

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    try {
      const payload = { title: form.title, owner_id: form.owner_id, due_date: form.due_date, due_time: form.due_time, priority: form.priority, notes: form.notes, sprint_id: form.sprint_id, linked: form.link_type && form.link_id ? { type: form.link_type, id: form.link_id } : null };
      const response = await api.post("/tasks", payload);
      setForm(empty);
      setError("");
      onCreated(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="task-form" data-testid="task-create-form" onSubmit={submit}>
      <div className="section-heading"><div><h3><ClipboardPlus size={17} /> New task</h3><p>Exactly one owner and a due date are required. Use student initials only.</p></div></div>
      <div className="task-form-grid">
        <label className="task-form-wide">Title<input data-testid="task-title-input" required minLength={3} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
        <label>Owner
          <select data-testid="task-owner-select" required value={form.owner_id} onChange={(e) => setForm({ ...form, owner_id: e.target.value })}>
            <option value="">Select owner</option>
            {owners.map((o) => <option key={o.id} value={o.id}>{`${o.name} · ${roleLabel(o.role)}`}</option>)}
          </select>
        </label>
        <label>Priority
          <select data-testid="task-priority-select" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
            {["High", "Normal", "Low"].map((p) => <option key={p}>{p}</option>)}
          </select>
        </label>
        <label>Due date<input data-testid="task-due-date-input" required type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></label>
        <label>Due time (ET, optional)<input data-testid="task-due-time-input" type="time" value={form.due_time} onChange={(e) => setForm({ ...form, due_time: e.target.value })} /></label>
        <label>Linked record type
          <select data-testid="task-link-type-select" value={form.link_type} onChange={(e) => setForm({ ...form, link_type: e.target.value, link_id: "" })}>
            {Object.entries(LINK_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label>Linked record
          <select data-testid="task-link-id-select" disabled={!form.link_type || !options} value={form.link_id} onChange={(e) => setForm({ ...form, link_id: e.target.value })}>
            <option value="">{form.link_type ? "Select record" : "—"}</option>
            {form.link_type && options && options[form.link_type].map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </label>
        <label>Sprint (optional)
          <select data-testid="task-sprint-select" value={form.sprint_id} onChange={(e) => setForm({ ...form, sprint_id: e.target.value })}>
            <option value="">No sprint</option>
            {sprints.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.start_date} → {s.end_date})</option>)}
          </select>
        </label>
        <label className="task-form-wide">Notes<textarea data-testid="task-notes-input" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Student initials only — never full names." /></label>
      </div>
      {error && <p className="form-message error-message" data-testid="task-form-error">{error}</p>}
      <div className="button-row">
        <button type="submit" className="button button-primary" data-testid="task-create-submit" disabled={busy || !form.owner_id || !form.due_date || form.title.trim().length < 3}>{busy ? "Saving…" : "Create task"}</button>
        <button type="button" className="button button-ghost" data-testid="task-create-cancel" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
};
